import type { ActivityEntry, ActivityKind, AgentKey, SwarmAction, SwarmState } from "./swarmTypes";
import { INITIAL_SWARM_STATE, TOOL_TO_AGENT } from "./swarmTypes";

const TOOL_LABELS: Record<string, string> = {
  analyze_video_intel:           "Analyzing video intel",
  prepare_and_analyze_video:     "Processing video stream",
  analyze_document_intel:        "Reading documents",
  discover_competitor_documents: "Discovering filings",
  analyze_web_signals:           "Scanning web signals",
  scrape_competitor_url:         "Scraping competitor page",
  monitor_competitor_news:       "Monitoring news feed",
  search_historical_intel:       "Searching history",
  store_intel_vector:            "Storing intel vector",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, " ");
}

function cappedLog(log: ActivityEntry[], cap = 8): ActivityEntry[] {
  return log.length > cap ? log.slice(log.length - cap) : log;
}

function appendLog(log: ActivityEntry[], entry: ActivityEntry, cap = 8): ActivityEntry[] {
  return cappedLog([...log, entry], cap);
}

export function swarmReducer(state: SwarmState, action: SwarmAction): SwarmState {
  switch (action.type) {
    case "RESET":
      return { ...INITIAL_SWARM_STATE };

    case "RUN_STARTED":
      return {
        ...INITIAL_SWARM_STATE,
        runStatus: "running",
        startedAt: Date.now(),
      };

    case "RUN_FINISHED":
      return {
        ...state,
        // Preserve awaiting-approval if set before RUN_FINISHED arrives
        runStatus: state.runStatus === "awaiting-approval" ? "awaiting-approval" : "done",
        elapsedMs: action.elapsedMs,
        agents: markRemainingAgentsDone(state.agents),
      };

    case "RUN_ERROR":
      return {
        ...state,
        runStatus: "error",
        errorMessage: action.message,
        elapsedMs: state.startedAt ? Date.now() - state.startedAt : null,
        agents: markRemainingAgentsDone(state.agents),
      };

    case "TOOL_CALL_STARTED": {
      // Prefer the backend's explicit agentKey hint (set on the swarm path for
      // MCP tools absent from TOOL_TO_AGENT); fall back to the canonical
      // tool→agent map for orchestrator @tool names.
      const agentKey = action.agentKey ?? TOOL_TO_AGENT[action.toolCallName] ?? null;
      if (!agentKey) return state;

      const prev = state.agents[agentKey];
      const label = toolLabel(action.toolCallName);
      const entry: ActivityEntry = { ts: Date.now(), text: label, kind: "tool" };
      return {
        ...state,
        agents: {
          ...state.agents,
          [agentKey]: {
            ...prev,
            status: "running",
            startedAt: prev.startedAt ?? Date.now(),
            currentStep: label,
            activityLog: appendLog(prev.activityLog, entry),
            toolCalls: [
              ...prev.toolCalls,
              { toolCallId: action.toolCallId, name: action.toolCallName, startedAt: Date.now(), finishedAt: null },
            ],
          },
        },
        toolCallToAgent: { ...state.toolCallToAgent, [action.toolCallId]: agentKey },
      };
    }

    case "TOOL_CALL_ENDED": {
      const agentKey = state.toolCallToAgent[action.toolCallId];
      if (!agentKey) return state;

      const now = Date.now();
      const prev = state.agents[agentKey];
      const updatedCalls = prev.toolCalls.map((tc) =>
        tc.toolCallId === action.toolCallId ? { ...tc, finishedAt: now } : tc
      );
      const allSettled = updatedCalls.every((tc) => tc.finishedAt !== null);
      const finishedTool = prev.toolCalls.find((tc) => tc.toolCallId === action.toolCallId);
      const doneLabel = finishedTool ? `${toolLabel(finishedTool.name)} done` : "Tool complete";
      const doneEntry: ActivityEntry = { ts: now, text: doneLabel, kind: "done" };
      let newLog = appendLog(prev.activityLog, doneEntry);

      // Parse buffered arg JSON and add a human-readable arg entry
      const buffer = prev.toolArgBuffer?.[action.toolCallId] ?? "";
      if (buffer) {
        try {
          const parsed = JSON.parse(buffer) as Record<string, unknown>;
          const val = parsed.url ?? parsed.query ?? parsed.competitor ?? parsed.ir_url ?? parsed.s3_uri ?? "";
          const firstKey = Object.keys(parsed)[0] ?? "";
          let argLabel = "";
          if (val) argLabel = `↳ ${String(val).slice(0, 60)}`;
          else if (firstKey) argLabel = `↳ {${firstKey}: ...}`;
          if (argLabel) {
            newLog = cappedLog([...newLog, { ts: now, text: argLabel, kind: "info" as ActivityKind }]);
          }
        } catch {
          // partial / malformed JSON - skip silently
        }
      }

      return {
        ...state,
        agents: {
          ...state.agents,
          [agentKey]: {
            ...prev,
            status: allSettled ? "done" : "running",
            finishedAt: allSettled ? now : null,
            currentStep: allSettled ? "Complete" : prev.currentStep,
            activityLog: newLog,
            toolCalls: updatedCalls,
          },
        },
      };
    }

    case "AGENT_DONE": {
      const prev = state.agents[action.agentKey];
      const now = Date.now();
      return {
        ...state,
        agents: {
          ...state.agents,
          [action.agentKey]: {
            ...prev,
            status: "done",
            finishedAt: prev.finishedAt ?? now,
            currentStep: "",
          },
        },
      };
    }

    case "AGENT_STEP": {
      const prev = state.agents[action.agentKey];
      const entry: ActivityEntry = { ts: Date.now(), text: action.text, kind: action.kind };
      return {
        ...state,
        agents: {
          ...state.agents,
          [action.agentKey]: {
            ...prev,
            // Transition idle→running on first step (swarm path emits STEP_STARTED, not TOOL_CALL_START)
            status: prev.status === "idle" ? "running" : prev.status,
            startedAt: prev.startedAt ?? Date.now(),
            currentStep: action.text,
            activityLog: appendLog(prev.activityLog, entry),
          },
        },
      };
    }

    case "TOOL_CALL_ARGS_CHUNK": {
      // Find which agent owns this toolCallId and accumulate the streaming arg delta
      for (const key of Object.keys(state.agents) as AgentKey[]) {
        const slot = state.agents[key];
        const tc = slot.toolCalls.find((t) => t.toolCallId === action.toolCallId);
        if (tc) {
          const prev = slot.toolArgBuffer ?? {};
          return {
            ...state,
            agents: {
              ...state.agents,
              [key]: {
                ...slot,
                toolArgBuffer: {
                  ...prev,
                  [action.toolCallId]: (prev[action.toolCallId] ?? "") + action.delta,
                },
              },
            },
          };
        }
      }
      return state;
    }

    case "AGENT_TEXT_CHUNK": {
      const prev = state.agents[action.agentKey];
      return {
        ...state,
        agents: {
          ...state.agents,
          [action.agentKey]: {
            ...prev,
            status: prev.status === "idle" ? "running" : prev.status,
            startedAt: prev.startedAt ?? Date.now(),
            streamText: prev.streamText + action.delta,
          },
        },
      };
    }

    case "TEXT_CHUNK":
      return { ...state, synthesisText: state.synthesisText + action.delta };

    case "THREAT_SCORE_SET":
      return { ...state, threat: action.threat };

    case "BATTLECARD_SET":
      return { ...state, battlecard: action.battlecard };

    case "SET_AWAITING_APPROVAL":
      return { ...state, runStatus: "awaiting-approval" };

    case "APPROVE":
      return { ...state, runStatus: "done" };

    default:
      return state;
  }
}

function markRemainingAgentsDone(agents: SwarmState["agents"]): SwarmState["agents"] {
  const now = Date.now();
  const updated = { ...agents };
  for (const key of Object.keys(updated) as AgentKey[]) {
    if (updated[key].status === "running") {
      updated[key] = { ...updated[key], status: "done", finishedAt: now };
    }
  }
  return updated;
}
